/* eslint-disable no-unused-expressions */
/* eslint-disable array-callback-return */
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import {
  Breadcrumbs,
  BackButton,
  Text,
  Wrapper,
  Button,
  Loading,
} from "@networkraildigitalfactory/components";
import PrepDetail from "./PrepDetail";
import CommentList from "../CommentList/CommentList";
import CreateComment from "../CreateComment/CreateComment";
import StatusLabel from "../StatusLabel/StatusLabel";
import { useMyPerformance, useObjectives, useUsers } from "../../hooks/useAPI";
import { capitalise } from "../../services/utils";
import EvidenceList from "../EvidenceList/EvidenceList";
import { AccessDeniedModal } from "../Modals";
import ViewPrepHeading from "./ViewPrepHeading";
import { useFileDownload } from "../../hooks/useFileDownload";

import styles from "./ViewReviewPrep.module.css";

const ViewReviewPrep = (props) => {
  const history = useHistory();
  const params = useParams();

  const { downloadFile } = useFileDownload();
  const { financialYear, email, reviewType } = params;
  const { user, isDirtyForm, setIsDirtyForm, setNavModalProps } = props;

  const [isLoading, setLoading] = useState(false);
  const [currentPrep, setCurrentPrep] = useState();
  const [currentEmployee, setCurrentEmployee] = useState();
  const [toggleAccessModal, setToggleAccessModal] = useState(false);
  const [employeeObjectives, setEmployeeObjectives] = useState();

  let isLineManager = localStorage.getItem("isLineManager");
  if (isLineManager && currentPrep?.email !== user.email) {
    isLineManager = true;
  } else {
    isLineManager = false;
  }

  const evidenceEmail = isLineManager ? email : user.email;

  const eoyEvidencePath = `${evidenceEmail}/${financialYear}/EOY/`;
  const interimEvidencePath = `${evidenceEmail}/${financialYear}/Interim/`;

  const navTo = (dest) => {
    history.push(dest);
  };

  const goBack = () => {
    const previousPath = isLineManager ? `/my-team/${email}/Performance` : "/my-performance";
    history.push(previousPath);
  };

  const openAccessModal = () => {
    setToggleAccessModal(true);
  };

  const onCloseAccessModal = () => {
    setToggleAccessModal(false);
    navTo("/my-team/");
  };

  const displayAccessModal = (postError) => {
    if (postError && postError.toString().includes("401")) {
      openAccessModal();
    }
  };

  const [, getEmployeeBeingViewed] = useUsers({ url: email }, { manual: true });
  const fetchEmployeeBeingViewed = async () => {
    try {
      const employee = await getEmployeeBeingViewed();
      if (employee.data) {
        setCurrentEmployee(employee.data.fullName);
      } else {
        setCurrentEmployee(null);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const formattedFY = financialYear.replace("-", "/");
  const interimPrep = `interim`;
  const reviewTypeLowerCase = reviewType.toLowerCase();
  const reviewTypePrep = currentPrep?.[reviewTypeLowerCase]?.[`${reviewTypeLowerCase}Prep`];
  const reviewTypeProgress = `${reviewTypeLowerCase}Progress`;
  const interimPrepProgress = `${interimPrep}Progress`;
  const isPrepCompleted = reviewTypePrep?.[`is${capitalise(reviewTypeLowerCase)}PrepCompleted`];
  const reviewTypeOverallComments = reviewTypePrep?.[`${reviewTypeLowerCase}OverallComments`];
  const interimOverallComments =
    currentPrep?.[interimPrep]?.[`${interimPrep}Prep`]?.[`${interimPrep}OverallComments`];
  const reviewTypeCommentList =
    currentPrep?.[reviewTypeLowerCase]?.[`${reviewTypeLowerCase}Comments`];

  const [{ error: getPrepError }, getPrepByFinancialYear] = useMyPerformance(
    {
      url: `/${financialYear}/${email || user.email}`,
    },
    { manual: true }
  );

  const fetchPrepByFinancialYear = async () => {
    try {
      setLoading(true);
      const prep = await getPrepByFinancialYear();
      if (prep.data.length > 0) {
        setCurrentPrep(prep.data[0]);
      } else {
        setCurrentPrep(null);
      }
      setTimeout(() => {
        setLoading(false);
      }, 325);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    displayAccessModal(getPrepError);
  }, [getPrepError]);

  useEffect(() => {
    fetchPrepByFinancialYear();
    fetchEmployeeBeingViewed();
  }, []);

  const status =
    currentPrep?.[reviewTypeLowerCase] && capitalise(currentPrep?.[reviewTypeLowerCase]?.status);

  const [, executeObjectiveAction] = useObjectives(
    {
      url: "reviewAdmin",
      method: "POST",
    },
    { manual: true }
  );

  const getEmployeeReviewObjectives = async () => {
    try {
      const employeeReviewObjectives = await executeObjectiveAction({
        data: { employeeEmail: email },
      });
      setEmployeeObjectives(employeeReviewObjectives.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (isLineManager && email) {
      getEmployeeReviewObjectives();
    }
  }, []);

  const isInterimPrepCompleted = currentPrep?.interim?.interimPrep?.isInterimPrepCompleted;
  const isEoyPrepCompleted = currentPrep?.eoy?.eoyPrep?.isEoyPrepCompleted;

  const getPrepDetails = () => {
    const prepDetails = [];
    let objectives = [];
    if (reviewType === "Interim") {
      objectives =
        currentPrep?.objectives.length > 0 ? currentPrep?.objectives : employeeObjectives;
    } else {
      objectives = currentPrep?.objectives;
    }
    objectives?.length > 0 &&
      objectives.map((objectivePrepDetails) => {
        if (reviewType === "Interim") {
          prepDetails.push({
            name: objectivePrepDetails?.objective?.name || objectivePrepDetails.name,
            comment:
              (isInterimPrepCompleted && objectivePrepDetails?.[reviewTypeProgress]) ||
              "No prep completed",
          });
        } else if (reviewType === "EOY") {
          prepDetails.push({
            name: objectivePrepDetails?.objective?.name || objectivePrepDetails.name,
            comment:
              (isEoyPrepCompleted && objectivePrepDetails?.[reviewTypeProgress]) ||
              "No prep completed",
            interimComment:
              (isInterimPrepCompleted && objectivePrepDetails?.[interimPrepProgress]) ||
              "No prep completed",
          });
        }
      });

    reviewType === "EOY"
      ? prepDetails.push({
          name: "Overall comments",
          comment: (isEoyPrepCompleted && reviewTypeOverallComments) || "No prep completed",
          interimComment: (isInterimPrepCompleted && interimOverallComments) || "No prep completed",
        })
      : prepDetails.push({
          name: "Overall comments",
          comment: (isInterimPrepCompleted && reviewTypeOverallComments) || "No prep completed",
        });

    return prepDetails;
  };

  const messageDetails = () => {
    let statusMessage;
    if (status === "Completed") {
      statusMessage =
        "This review has been marked as complete. You can add comments below if you wish.";
    } else if (isLineManager && status !== "Completed") {
      statusMessage = `You’ll find ${
        currentEmployee?.split(" ")[0]
      }'s prep for this review below. You can add comments to this review, or start the review by selecting ‘Begin review’.`;
    } else if (!isLineManager && status !== "Completed") {
      statusMessage = "These notes can be edited up until your review with your line manager.";
    }
    return statusMessage;
  };

  const breadCrumbMessage = () => {
    let message = "";
    if (reviewType === "Interim") {
      message = `${reviewTypeLowerCase}`;
    } else if (reviewType === "EOY") {
      message = `${reviewType}`;
    }
    return message;
  };

  const prepDetails = getPrepDetails();

  const breadcrumbs = isLineManager
    ? [
        {
          text: "My team",
          onClick: () => {
            if (isDirtyForm) {
              setNavModalProps({ show: true, navPath: "/my-team" });
            } else {
              navTo("/my-team");
            }
          },
        },
        {
          text: currentEmployee,
          onClick: () => {
            if (isDirtyForm) {
              setNavModalProps({
                show: true,
                navPath: isLineManager ? `/my-team/${email}/Performance` : "/my-performance",
              });
            } else {
              goBack();
            }
          },
        },
        {
          text:
            status === "Completed"
              ? `Completed ${breadCrumbMessage()} review`
              : `${reviewType} review prep`,
        },
      ]
    : [
        {
          text: "My performance",
          onClick: () => {
            if (isDirtyForm) {
              setNavModalProps({
                show: true,
                navPath: isLineManager ? `/my-team/${email}/Performance` : "/my-performance",
              });
            } else {
              goBack();
            }
          },
        },
        {
          text:
            status === "Completed"
              ? `Completed ${breadCrumbMessage()} review`
              : `${reviewType} review prep`,
        },
      ];

  const viewPrepTop = () => {
    const multiEOYAlert = [];
    if (reviewType === "EOY") {
      multiEOYAlert.push({
        heading: "Line Manager’s Interim comment",
        message: currentPrep?.interim?.interimFeedback,
      });
      multiEOYAlert.push({
        heading: "Line Manager’s EOY comment",
        message: currentPrep?.eoy?.eoyFeedback,
      });
    }
    return (
      <ViewPrepHeading
        currentPrep={currentPrep}
        reviewTypeLowerCase={reviewTypeLowerCase}
        multiEOYAlert={multiEOYAlert}
        isPrepCompleted={isPrepCompleted}
        status={status}
      />
    );
  };

  return (
    <>
      <Loading loading={isLoading} />
      <div className={styles.breadcrumbs_wrapper}>
        <Breadcrumbs items={breadcrumbs} />
      </div>
      {!isLoading && currentPrep && (
        <>
          <Wrapper maxWidth={1350} align="center">
            <div className={styles.row}>
              <BackButton
                text={`back to ${isLineManager ? "" : "my"} performance`}
                onClick={() => {
                  if (isDirtyForm) {
                    setNavModalProps({
                      show: true,
                      navPath: isLineManager ? `/my-team/${email}/Performance` : "/my-performance",
                    });
                  } else {
                    goBack();
                  }
                }}
                style={{ lineHeight: "140%", letterSpacing: "0.0015em" }}
              />
              {status && status !== "Draft" && <StatusLabel status={status} />}
            </div>
            <div className={styles.row}>
              <Text wrapper="span" type="body">
                {messageDetails()}
              </Text>
            </div>
            <div className={styles.light_bg}>
              <Text wrapper="h2" type="header" style={{ marginBottom: "5px" }}>
                {formattedFY} {reviewType} Review {status === "Completed" ? "" : "Prep"}
              </Text>
              {viewPrepTop()}
              {prepDetails.map((detail, index) => (
                <PrepDetail key={index} detail={detail} reviewType={reviewType} />
              ))}
              <Text
                wrapper="h2"
                type="header"
                style={{
                  fontSize: "18px",
                  color: "#353535",
                  paddingLeft: "75px",
                  marginBottom: "12px",
                  marginTop: "20px",
                }}
              >
                Evidence
              </Text>
              <div className={styles.detail_row}>
                <Text wrapper="span" type="body" style={{ width: "75px" }}>
                  Interim:
                </Text>
                {currentPrep?.interim?.interimPrep?.isInterimPrepCompleted &&
                currentPrep?.interim?.interimPrep?.interimEvidence?.length > 0 ? (
                  <EvidenceList
                    evidenceData={currentPrep?.interim?.interimPrep?.interimEvidence}
                    evidencePath={interimEvidencePath}
                    downloadFile={downloadFile}
                  />
                ) : (
                  "No evidence uploaded"
                )}
              </div>
              {reviewType === "EOY" && (
                <div className={styles.detail_row_EOY}>
                  <Text wrapper="span" type="body" style={{ width: "75px" }}>
                    EOY:
                  </Text>
                  {currentPrep?.eoy?.eoyPrep?.isEoyPrepCompleted &&
                  currentPrep?.eoy?.eoyPrep?.eoyEvidence?.length > 0 ? (
                    <EvidenceList
                      evidenceData={currentPrep?.eoy?.eoyPrep?.eoyEvidence}
                      evidencePath={eoyEvidencePath}
                      downloadFile={downloadFile}
                    />
                  ) : (
                    "No evidence uploaded"
                  )}
                </div>
              )}
            </div>
            <div className={styles.button_row}>
              {status === "Completed" || currentPrep?.eoy?.isEoyCompleted ? (
                <hr />
              ) : (
                <Button
                  text={isLineManager ? "Begin review" : "Edit"}
                  name={isLineManager ? "Begin review" : "Edit"}
                  onClick={() =>
                    navTo(
                      isLineManager
                        ? `/my-team/${email}/begin-review/${financialYear}/${reviewType}`
                        : `/edit-review-prep/${financialYear}/${reviewType}`
                    )
                  }
                  color="blue"
                />
              )}
            </div>
            {status === "Completed" && (
              <div className={styles.column}>
                <CreateComment
                  user={user}
                  isLineManager={isLineManager}
                  type={`${reviewTypeLowerCase}Prep`}
                  reviewPrepId={currentPrep?._id}
                  getUpdatedReview={fetchPrepByFinancialYear}
                  setIsDirtyForm={setIsDirtyForm}
                />
                <CommentList
                  data={reviewTypeCommentList || []}
                  user={user}
                  isLineManager={isLineManager}
                  type="comment"
                />
              </div>
            )}
          </Wrapper>
          <AccessDeniedModal show={toggleAccessModal} close={onCloseAccessModal} />
        </>
      )}
    </>
  );
};

export default ViewReviewPrep;
